/**
 * Form Data Transformers for Step 4 (Manual Screening Questions)
 * 
 * Handles transformation between form state, store data, and AI responses
 * Adapted for React Hook Form (JobPostFormData)
 */

import { JobPostFormData } from '../../../schemas/jobPostValidation';
import { JobDescriptionEntity } from '../../../../../Entities/JobDescription';
import { DEFAULT_PASS_PERCENTAGE } from '../../../utils/constants';

/**
 * Local state type for manual screening questions (used in component)
 */
export interface ManualScreeningQuestionState {
  id: string | number;
  description: string;
  order: number;
  _isExisting?: boolean; // Flag to track existing questions in edit mode
}

/**
 * Transform manual screening questions array to Step4Data (form format)
 */
export const transformFormToStep4Data = (screeningQuestions: ManualScreeningQuestionState[]): JobPostFormData['step4'] => {
  const transformedQuestions = screeningQuestions.map((q, index) => ({
    id: typeof q.id === 'number' ? String(q.id) : q.id,
    description: q.description.trim(),
    order: q.order || index + 1,
  }));

  return {
    manualScreeningQuestions: transformedQuestions,
  };
};

/**
 * Transform Step4Data to local state (for component)
 */
export const transformStoreToStep4 = (step4Data: JobPostFormData['step4'] | null | undefined): ManualScreeningQuestionState[] => {
  if (!step4Data || !step4Data.manualScreeningQuestions) {
    return [];
  }

  return step4Data.manualScreeningQuestions.map((q) => ({
    id: q.id,
    description: q.description || '',
    order: q.order,
  }));
};

/**
 * Map JobDescriptionEntity to Step4Data (for edit/clone scenarios)
 * Extracts Job Alignment from interviewRounds and manual questions from additionalRequirements
 */
export const mapJobDescriptionToStep4 = (jobPost: JobDescriptionEntity): JobPostFormData['step4'] | null => {
  // Extract Job Alignment round from interviewRounds
  const jobAlignmentRound = jobPost.interviewRounds?.find(
    (round) => round.interviewRoundId === 'job-alignment'
  );

  // Always load manual questions if they exist (independent of isAIGenerated)
  // Manual questions and AI questions are optional and independent
  // Both can coexist - manual questions are preserved even when isAIGenerated is true
  const manualQuestions = (jobPost.additionalRequirements || [])
    .filter((req) => req.topic === 'manual_questions')
    .map((req, index) => ({
      id: String(index + 1),
      description: req.description || '',
      order: index + 1,
    }));

  // Extract Job Alignment configuration
  // Only create jobAlignment if the round existed OR if there are manual questions
  // If neither exists, leave jobAlignment undefined (Job Alignment was not configured)
  const hasJobAlignment = jobAlignmentRound != null || manualQuestions.length > 0;
  
  const jobAlignment = hasJobAlignment ? {
    isAIGenerated: jobAlignmentRound?.isAIGenerated ?? false, // Default to false if no round but has manual questions
    passPercentage: jobAlignmentRound?.passPercentage ?? DEFAULT_PASS_PERCENTAGE,
    isMustQualify: jobAlignmentRound?.isMustQualify ?? true,
  } : undefined;

  return {
    jobAlignment,
    manualScreeningQuestions: manualQuestions,
  };
};

/**
 * Map AI response to Step4Data format
 */
export const mapAIResponseToStep4Data = (aiResponse: any): JobPostFormData['step4'] => {
  // AI response structure may vary - adapt based on actual API response
  const questions: Array<{ id: string | number; description: string; order: number }> = [];
  
  if (aiResponse?.manualScreeningQuestions && Array.isArray(aiResponse.manualScreeningQuestions)) {
    aiResponse.manualScreeningQuestions.forEach((q: any, index: number) => {
      if (q.description) {
        questions.push({
          id: q.id || String(index + 1),
          description: q.description,
          order: q.order || index + 1,
        });
      }
    });
  }
  
  return {
    manualScreeningQuestions: questions,
  };
};

/**
 * Merge AI-generated data with existing user data
 */
export const mergeAIWithUserData = (
  existingData: JobPostFormData['step4'] | null,
  aiData: JobPostFormData['step4']
): JobPostFormData['step4'] => {
  // For Manual Screening Questions, we'll replace existing questions with AI-generated ones
  // In future, can implement merge logic (e.g., append unique questions)
  return aiData;
};


